/**
 * Customer naming and quick-add defaults.
 *
 * Pure, so the rules that decide the step budget are unit-tested rather than
 * estimated — the same approach `wizard-machine.ts` takes to §2.2's import
 * budget. `apps/pos` has no DOM testing library by choice; logic that matters
 * gets extracted here instead of asserted through a rendered component.
 */

export interface NameParts {
  readonly firstName?: string | null | undefined;
  readonly lastName?: string | null | undefined;
  readonly companyName?: string | null | undefined;
}

/**
 * What to call this customer on screen.
 *
 * A company is a customer too — a race team or a school buys twenty pairs of
 * skis under one name and no person's name applies. Falls back to the company
 * only when there is no personal name, because when both exist the human is who
 * the counter is talking to.
 */
export function customerName(parts: NameParts, fallback = 'Unnamed customer'): string {
  const person = [parts.firstName, parts.lastName]
    .filter((part): part is string => part != null && part.trim() !== '')
    .join(' ')
    .trim();

  if (person !== '') return person;

  const company = parts.companyName?.trim() ?? '';
  return company === '' ? fallback : company;
}

export interface QuickAddDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly email: string;
}

const EMPTY_DRAFT: QuickAddDraft = { firstName: '', lastName: '', phone: '', email: '' };

/**
 * Turn what was typed into the search box into a half-filled new-customer form.
 *
 * This is the step budget (§2.2, "new customer quick add ≤5"): pressing *New
 * customer* must not throw away the thing the operator already typed. Searching
 * for a name that is not on file and then retyping that same name into a blank
 * form is the interaction that pushes the flow over budget, and it is the one
 * that makes staff stop attaching customers at all.
 *
 * Smart defaults, §2.2-5 — the common path is confirmation, not data entry.
 */
export function quickAddDefaults(term: string): QuickAddDraft {
  const trimmed = term.trim();
  if (trimmed === '') return EMPTY_DRAFT;

  // An `@` can only have been an email attempt; nothing else in a customer
  // record contains one.
  if (trimmed.includes('@')) return { ...EMPTY_DRAFT, email: trimmed.toLowerCase() };

  /*
   * A phone number typed at a counter arrives in every shape: `5550137`,
   * `555-0137`, `(555) 000-0137`. What they have in common is that stripping
   * punctuation leaves nothing but digits — so that, plus enough digits to be a
   * real number, is the test. Seven is the shortest thing anyone would call a
   * phone number; below it the operator is likelier typing a house number in a
   * name search.
   */
  const digits = trimmed.replace(/\D/g, '');
  const punctuationOnly = /^[\d\s()+.-]+$/.test(trimmed);
  if (punctuationOnly && digits.length >= 7) return { ...EMPTY_DRAFT, phone: trimmed };

  const [first = '', ...rest] = trimmed.split(/\s+/);
  return { ...EMPTY_DRAFT, firstName: first, lastName: rest.join(' ') };
}
